import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import SaveSpreadsheetColumnsMappingForm from 'bulk-import/panes/SaveSpreadsheetColumnsMappingForm';
import StudyWarningsTable from 'bulk-import/panes/StudyWarningsTable';
import ValidationErrors from 'bulk-import/panes/ValidationErrors';
import {SpreadsheetType} from 'bulk-import/panes/ValidationErrorsTable';
import * as SpreadsheetBulkImportActions from 'bulk-import/SpreadsheetBulkImportActions';
import {
  CompletedSpreadsheetValidationErrorInfo,
  DerivedColumnsData,
  ErrorData,
  IMPORT_TYPE,
  ImportSourceType,
  OPTIONS,
  RowErrorCode,
  SelectedSchemaIds,
  SpreadsheetBulkImportPreferences,
  SpreadsheetColumn,
  SpreadsheetValidationErrorType,
  TaskState,
} from 'bulk-import/SpreadsheetBulkImportConstants';
import SpreadsheetBulkImportOption from 'bulk-import/SpreadsheetBulkImportOption';
import * as SpreadsheetBulkImportUtil from 'bulk-import/SpreadsheetBulkImportUtil';
import {SampleControlsFeatureLevel} from 'inventory/sample-status/SampleControlsFeatureLevel';
import {hasMinimumSCRFeatureLevel} from 'inventory/sample-status/SampleControlsFeatureLevelUtil';
import preferences from 'Preferences';
import MergeDuplicatePopover from 'registry/MergeDuplicatePopover';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {ButtonStyle} from 'ui-library/buttons/Button';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import Modal from 'ui-library/modals/Modal';

import ModalButtonKey from '../../test/lib/cypress/shared/ModalButtonKey';

interface State {
  isSaveSpreadsheetColumnsMappingFormChecked: boolean;
}

export interface ShowErrorsModalPaneProps {
  derivedColumnsData: DerivedColumnsData;
  errorData: ErrorData | null;
  fileDataColumns: Array<SpreadsheetColumn>;
  importType: IMPORT_TYPE;
  isLastPane: boolean;
  modalProps: {
    title: string;
    onRequestClose: () => void;
  };
  onImport: () => void;
  options: {
    [key: string]: boolean;
  };
  selectedSchemaIds: SelectedSchemaIds;
  enableReplaceLinkWithText: boolean;
  enableSkipErrors: boolean;
  customHandleGoToPrevPaneFn?: (paneProps: ShowErrorsModalPaneProps) => void;
  uploadFlow?: string;
}

export interface ShowErrorsModalPaneConfiguration {
  enableReplaceLinkWithText: boolean;
  enableSkipErrors: boolean;
  customHandleGoToPrevPaneFn?: (paneProps: ShowErrorsModalPaneProps) => void;
  uploadFlow?: string;
}

interface ShowErrorsModalPaneConfigurationArgs {
  enableReplaceLinkWithText: boolean;
  enableSkipErrors: boolean;
  customHandleGoToPrevPaneFn?: (paneProps: ShowErrorsModalPaneProps) => void;
  uploadFlow?: string;
}

interface ResolvableErrorSetting {
  type: string;
  errorType: SpreadsheetValidationErrorType;
  renderCondition: boolean;
  filter: (errorCode: string) => boolean;
  text: string;
}

class ShowErrorsModalPane extends React.Component<ShowErrorsModalPaneProps> {
  /**
   * Returns configuration for ShowErrorsModalPane for a given import flow.
   * @param {enableReplaceLinkWithText} If enabled, shows option to use text value for any link errors.
   * @param {enableSkipErrors} If enabled, shows option to skip errors.
   * @param {customHandleGoToPrevPaneFn} Custom handler to go to the previous pane, required if the flow
   *                                     needs to handle skipping or branching panes
   * @param {uploadFlow} The upload flow of the import
   */
  static configure({
    enableReplaceLinkWithText,
    enableSkipErrors,
    customHandleGoToPrevPaneFn,
    uploadFlow,
  }: ShowErrorsModalPaneConfigurationArgs): ShowErrorsModalPaneConfiguration {
    return {
      enableReplaceLinkWithText,
      enableSkipErrors,
      customHandleGoToPrevPaneFn,
      uploadFlow,
    };
  }

  state: State = {
    isSaveSpreadsheetColumnsMappingFormChecked: false,
  };

  private setIsSaveSpreadsheetColumnsMappingFormChecked = (checked: boolean): void => {
    this.setState({isSaveSpreadsheetColumnsMappingFormChecked: checked});
  };

  private getResolvableErrorSettings = (): Array<ResolvableErrorSetting> => [
    {
      type: OPTIONS.REPLACE_LINK_WITH_TEXT,
      errorType: SpreadsheetValidationErrorType.TAG_ERRORS,
      renderCondition: this.props.enableReplaceLinkWithText,
      filter: SpreadsheetBulkImportUtil.canApplyReplaceLinkWithText,
      text: 'Use text value for any linked items that cannot be matched.',
    },
    {
      type: OPTIONS.USE_EXISTING_CONTAINERS,
      errorType: SpreadsheetValidationErrorType.ITEM_NAME_ERRORS,
      renderCondition: true,
      filter: SpreadsheetBulkImportUtil.canUseExistingContainers,
      text: 'Transfer samples into existing containers',
    },
    {
      type: OPTIONS.USE_NON_EMPTY_CONTAINERS,
      errorType: SpreadsheetValidationErrorType.ITEM_NAME_ERRORS,
      renderCondition: true,
      filter: SpreadsheetBulkImportUtil.canUseNonEmptyContainers,
      text: 'Transfer samples into non-empty containers',
    },
    {
      type: OPTIONS.USE_SAMPLES_WITH_STATUS_WARNINGS,
      errorType: SpreadsheetValidationErrorType.ITEM_NAME_ERRORS,
      renderCondition: true,
      filter: SpreadsheetBulkImportUtil.canUseSamplesWithStatusWarnings,
      text: hasMinimumSCRFeatureLevel(SampleControlsFeatureLevel.V3)
        ? 'Override availability warnings'
        : 'Override authorization / availability warnings',
    },
  ];

  private resolvableErrorStatuses = (): {[key: string]: boolean} =>
    // Turns ResolvableErrorSettings into an object where the key is setting.type
    // and the value is whether the user has resolved that error depending on props
    // Ex. { useNonEmptyContainers: true, useExistingContainers: false }
    this.getResolvableErrorSettings().reduce((obj: {[key: string]: boolean}, setting) => {
      obj[setting.type] = this.props.options[setting.type];
      return obj;
    }, {});

  private errorDataIsCompleted = (
    errorData: ErrorData
  ): errorData is CompletedSpreadsheetValidationErrorInfo => errorData.state === TaskState.COMPLETED;

  private hasUnresolvedErrors = (): boolean =>
    this.props.errorData != null &&
    this.errorDataIsCompleted(this.props.errorData) &&
    SpreadsheetBulkImportUtil.hasUnresolvedErrors(this.props.errorData, this.resolvableErrorStatuses());

  private hasValidationPassed = (): boolean => {
    if (!this.props.errorData || this.props.errorData.state !== TaskState.COMPLETED) {
      return false;
    }
    return this.props.options[OPTIONS.SKIP_ERRORS] || !this.hasUnresolvedErrors();
  };

  private hasIncompleteErrors = (): boolean =>
    this.props.errorData != null && this.props.errorData.state === TaskState.COMPLETED;

  private shouldShowOption = (
    filter: (errorCode: string) => boolean,
    errorType: SpreadsheetValidationErrorType
  ): boolean =>
    this.errorDataIsCompleted(this.props.errorData!) &&
    this.props.errorData[errorType].some(({errorCode}: {errorCode: string}) => filter(errorCode));

  private shouldShowSkipErrors = (): boolean =>
    (BenchlingConfig.NAV_ENABLE_SKIP_SPREADSHEET_ROWS_WITH_ERRORS ||
      preferences.get(SpreadsheetBulkImportPreferences.SOURCE_TYPE) === ImportSourceType.SDF) &&
    // TODO(BNCH-101440) remove once BNCH-101440 is implemented
    this.props.errorData?.state === TaskState.COMPLETED &&
    !this.props.errorData.otherErrors.some((e) => e.errorCode === RowErrorCode.TOO_MANY_CONTAINERS_TO_MOVE);

  private getUnresolvedErrorsHelpText = (): string | null => {
    // For SDF import with failing molecules, users will not receive an email with omitted rows.
    if (preferences.get(SpreadsheetBulkImportPreferences.SOURCE_TYPE) !== ImportSourceType.SDF) {
      return ' The rows without errors will be imported and you will receive an email with the omitted rows.';
    }
    return null;
  };

  private handleImportFromSpreadsheet = (): void => {
    this.props.onImport();
  };

  render(): JSX.Element {
    const nextButtonText = this.props.isLastPane ? 'Import' : 'Next';
    return (
      <Modal
        enableRedesign={false}
        expandHeight={true}
        buttons={[
          {
            key: 'back-from-error-pane-btn',
            text: 'Back',
            onClick: () => {
              if (this.props.customHandleGoToPrevPaneFn) {
                this.props.customHandleGoToPrevPaneFn(this.props);
              } else {
                SpreadsheetBulkImportActions.goToPrevPane();
              }
            },
          },
          {
            key: 'next-from-error-pane-btn',
            text: nextButtonText,
            style: this.props.isLastPane ? ButtonStyle.PRIMARY : ButtonStyle.SECONDARY,
            onClick: this.props.isLastPane
              ? this.handleImportFromSpreadsheet
              : SpreadsheetBulkImportActions.goToNextPane,
            disabled: !this.hasValidationPassed() || this.state.isSaveSpreadsheetColumnsMappingFormChecked,
            dataTestAttrs: {
              component: 'ShowErrorsModal',
              element: 'footerButton',
              key: this.props.isLastPane ? ModalButtonKey.IMPORT : ModalButtonKey.NEXT,
            },
          },
        ]}
        {...this.props.modalProps}
      >
        {this.props.errorData?.state === TaskState.STARTED ? (
          <div className="text-center">
            <div className="u-small-padding">
              <HorizontalThrobber />
            </div>
            <p className="faded u-text-uppercase">Validating...</p>
          </div>
        ) : (
          <div>
            <ValidationErrors
              spreadsheetType={SpreadsheetType.SPREADSHEET}
              errorData={this.props.errorData}
              nextButtonText={nextButtonText}
              resolvableErrorStatuses={this.resolvableErrorStatuses()}
              selectedTagSchemaId={this.props.selectedSchemaIds?.tagSchemaId}
            />
            {this.props.importType === IMPORT_TYPE.SAMPLES && (
              <div>
                Once samples are imported, check the popup message to copy a list of created sample names to
                the clipboard. We will also email you a list of created sample names.
              </div>
            )}
            <div className="showErrorsPane-options">
              {this.hasIncompleteErrors() &&
                this.getResolvableErrorSettings().map(
                  (setting, index) =>
                    setting.renderCondition &&
                    this.shouldShowOption(setting.filter, setting.errorType) && (
                      <SpreadsheetBulkImportOption
                        isChecked={this.props.options[setting.type]}
                        name={setting.type}
                        text={setting.text}
                        key={index}
                      />
                    )
                )}
            </div>
            {this.props.enableSkipErrors &&
              this.props.errorData?.state === TaskState.COMPLETED &&
              this.hasUnresolvedErrors() && (
                <div className="u-small-spacing-top">
                  <InlineBanner
                    bannerType={InlineBannerType.ERROR}
                    text="There are unresolved errors in your spreadsheet."
                    supportingInfo={
                      this.shouldShowSkipErrors() && (
                        <SpreadsheetBulkImportOption
                          className="u-small-spacing-top"
                          isChecked={this.props.options[OPTIONS.SKIP_ERRORS]}
                          name={OPTIONS.SKIP_ERRORS}
                          text={
                            <span>
                              <b>Skip rows with unresolved errors.</b>
                              {this.getUnresolvedErrorsHelpText()}
                            </span>
                          }
                        />
                      )
                    }
                  />
                </div>
              )}
            {this.props.errorData?.registryWarnings?.some((warning) =>
              warning.hasOwnProperty('primaryDuplicateItemApiId')
            ) && (
              <MergeDuplicatePopover>
                <a>What happens when my duplicate entities get merged?</a>
              </MergeDuplicatePopover>
            )}
            {this.props.derivedColumnsData.state === TaskState.FAILED && (
              <InlineBanner
                bannerType={InlineBannerType.WARNING}
                text="We encountered an error while checking for additional mappings."
                supportingInfo="You can proceed with import, but note that data may be lost."
              />
            )}
            {
              // Hide the discrepancy errors table if there is a "too many containers to move" error and no other
              // row errors, because otherwise it would lie and say there were no errors.
              BenchlingConfig.REGISTRY_ENABLE_TRIAL_SAMPLES &&
                this.props.errorData &&
                this.props.errorData.state !== TaskState.FAILED && (
                  <StudyWarningsTable
                    spreadsheetType={SpreadsheetType.SPREADSHEET}
                    studyWarnings={this.props.errorData.studyWarnings}
                  />
                )
            }
            <SaveSpreadsheetColumnsMappingForm
              columns={this.props.fileDataColumns}
              selectedSchemaIds={this.props.selectedSchemaIds}
              uploadFlow={this.props.uploadFlow}
              isSaveColumnsMappingFormChecked={this.state.isSaveSpreadsheetColumnsMappingFormChecked}
              setIsSaveColumnsMappingFormChecked={this.setIsSaveSpreadsheetColumnsMappingFormChecked}
            />
          </div>
        )}
      </Modal>
    );
  }
}

export default ShowErrorsModalPane;
